import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useDropzone } from "react-dropzone";
import dayjs, { Dayjs } from "dayjs";
import CommonStyles from "@components/CommonStyles";
import { setAlertMessage } from "@base/store/reducers/appCommonSlice";
import { prepareAttachmentsForUpload } from "@base/helpers/attachmentUpload";
import { ILifeInsurance } from "@base/modules/patientInsurance/lifeInsurance.interface";
import patientInsuranceService from "@base/modules/patientInsurance/patientInsuranceService";
import { buildLifeInsuranceBody, getRemovedImageNames, groupProviders, IProviderGroup } from "@base/modules/patientInsurance/lifeInsuranceRules";

const MAX_PHOTOS = 10;
const SERVER_DATE = "YYYY-MM-DD";
const START_MIN = dayjs("1940-01-01");
const START_MAX = dayjs("2040-01-01");
const END_MIN = dayjs("1940-01-01");
const END_MAX = dayjs("3000-01-01");
const ACCEPTED = { "image/*": [".png", ".jpg", ".jpeg"] };

interface IProps {
  open: boolean;
  toggle: () => void;
  patientRecordCode: string;
  /** The record being edited; absent when adding. */
  insurance?: ILifeInsurance;
  onSaved: () => void;
}

/** Add or edit a health insurance. On an edit the insurer and plan are fixed; dates, note and photos are not. */
export default function LifeInsuranceForm({ open, toggle, patientRecordCode, insurance, onSaved }: IProps) {
  //! state
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const isEdit = !!insurance;

  const [groups, setGroups] = useState<IProviderGroup[]>([]);
  const [providerName, setProviderName] = useState<string>();
  const [planId, setPlanId] = useState<number>();
  const [startDate, setStartDate] = useState<Dayjs | null>(insurance?.start_date ? dayjs(insurance.start_date) : null);
  const [endDate, setEndDate] = useState<Dayjs | null>(insurance?.end_date ? dayjs(insurance.end_date) : null);
  const [description, setDescription] = useState<string>(insurance?.description ?? "");
  // Photos already on the record that are being kept, and new ones picked in this session.
  const [keptImages, setKeptImages] = useState(insurance?.images ?? []);
  const [newFiles, setNewFiles] = useState<File[]>([]);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const photoCount = keptImages.length + newFiles.length;

  //! function
  const notify = useCallback(
    (type: "success" | "error", description: string) => dispatch(setAlertMessage({ type, message: t("Notification"), description })),
    [dispatch, t],
  );

  // The plan list is only needed to pick one, which an edit cannot do.
  useEffect(() => {
    if (isEdit) {
      return;
    }

    (async () => {
      try {
        const resp = await patientInsuranceService.getLifeProviders(i18n.resolvedLanguage ?? "vi");
        const grouped = groupProviders(resp.data ?? []);

        setGroups(grouped);
        setProviderName(grouped[0]?.provider);
        setPlanId(grouped[0]?.plans[0]?.id);
      } catch (error) {}
    })();
  }, [isEdit, i18n.resolvedLanguage]);

  const plans = useMemo(() => groups.find((group) => group.provider === providerName)?.plans ?? [], [groups, providerName]);
  const selectedPlan = plans.find((plan) => plan.id === planId);

  const onChangeProvider = (value: string) => {
    setProviderName(value);
    // A different insurer starts on its own first plan.
    setPlanId(groups.find((group) => group.provider === value)?.plans[0]?.id);
  };

  const onDrop = useCallback(
    (accepted: File[]) => {
      const room = MAX_PHOTOS - photoCount;

      if (accepted.length > room) {
        notify("error", t("insurance.maxPhotos", { max: MAX_PHOTOS }));
      }

      setNewFiles((current) => [...current, ...accepted.slice(0, Math.max(room, 0))]);
    },
    [photoCount, notify, t],
  );

  const { getRootProps, getInputProps } = useDropzone({ accept: ACCEPTED, onDrop, disabled: photoCount >= MAX_PHOTOS });

  const validate = () => {
    const required = (label: string) => t("insurance.required", { label });

    if (!isEdit && !selectedPlan?.name) {
      return required(t("insurance.provider"));
    }

    if (!startDate) {
      return required(t("insurance.startDay"));
    }

    if (!endDate) {
      return required(t("insurance.endDay"));
    }

    return undefined;
  };

  const onSave = async () => {
    const error = validate();

    if (error) {
      notify("error", error);
      return;
    }

    try {
      setIsSaving(true);

      const body = buildLifeInsuranceBody(
        { plan: selectedPlan, startDate: startDate?.format(SERVER_DATE), endDate: endDate?.format(SERVER_DATE), description },
        patientRecordCode,
        insurance,
      );
      const resp = await patientInsuranceService.saveLife(body, i18n.resolvedLanguage ?? "vi");
      const savedId = resp.data?.id ?? insurance?.id;

      // Removed photos go one by one before the new ones are added.
      const removed = getRemovedImageNames(
        insurance?.images,
        keptImages.map((image) => String(image.link)),
      );

      for (const name of removed) {
        await patientInsuranceService.deleteLifeFile(name);
      }

      const prepared = await prepareAttachmentsForUpload(newFiles);

      if (savedId && prepared.length) {
        await patientInsuranceService.uploadLifeFiles(savedId, prepared);
      }

      notify("success", t("Successfully") + "!");
      onSaved();
    } catch (saveError) {
      notify("error", t("Failed") + "!");
    } finally {
      setIsSaving(false);
    }
  };

  const onLockedFieldClick = () => {
    if (isEdit) {
      notify("error", t("insurance.fieldCannotChange"));
    }
  };

  //! render
  return (
    <CommonStyles.ModalConfirmationUI
      open={open}
      toggle={toggle}
      type="normal"
      width={640}
      title={isEdit ? t("insurance.editTitle") : t("insurance.addTitle")}
      okText={t("insurance.save")}
      okButtonProps={{ loading: isSaving }}
      onConfirm={onSave}
      content={
        <div className="flex flex-col gap-4 max-h-[65vh] overflow-y-auto pr-1">
          <div onClick={onLockedFieldClick}>
            <CommonStyles.SelectUI
              variant="filled"
              required
              disabled={isEdit}
              label={t("insurance.packageType")}
              placeholder={t("insurance.selectPackageType")}
              value={isEdit ? insurance?.health_insurance?.provider : providerName}
              options={groups.map((group) => ({ label: group.provider, value: group.provider }))}
              onChange={onChangeProvider}
            />
          </div>

          <div onClick={onLockedFieldClick}>
            <CommonStyles.SelectUI
              variant="filled"
              required
              disabled={isEdit}
              label={t("insurance.customerGroup")}
              placeholder={t("insurance.selectCustomerGroup")}
              value={isEdit ? insurance?.health_insurance?.name : planId}
              options={plans.map((plan) => ({ label: plan.name, value: plan.id }))}
              onChange={(value: number) => setPlanId(value)}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <CommonStyles.DatePickerUI
              variant="filled"
              required
              label={t("insurance.effectiveDate")}
              format="DD/MM/YYYY"
              value={startDate}
              minDate={START_MIN}
              // The start may not come after the chosen end.
              maxDate={endDate ?? START_MAX}
              onChange={(value: Dayjs | null) => setStartDate(value)}
            />

            <CommonStyles.DatePickerUI
              variant="filled"
              required
              label={t("insurance.expirationDate")}
              format="DD/MM/YYYY"
              value={endDate}
              minDate={startDate ?? END_MIN}
              maxDate={END_MAX}
              onChange={(value: Dayjs | null) => setEndDate(value)}
            />
          </div>

          <CommonStyles.TextAreaUI
            variant="filled"
            label={t("insurance.note")}
            value={description}
            onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => setDescription(event.target.value)}
          />

          <div className="flex flex-col gap-2">
            <div className="flex justify-between items-center">
              <span className="text-common-maintext text-sm font-medium font-['Inter']">{t("insurance.attachments")}</span>
              <span className={`text-xs font-normal font-['Inter'] ${photoCount >= MAX_PHOTOS ? "text-common-primary" : "text-common-subtext"}`}>
                {photoCount ? t("insurance.maxPhotosCount", { max: MAX_PHOTOS, count: photoCount }) : t("insurance.maxPhotos", { max: MAX_PHOTOS })}
              </span>
            </div>

            <div
              {...getRootProps()}
              className={`w-full border border-dashed border-common-divider rounded-lg p-4 text-center text-sm font-normal font-['Inter'] ${
                photoCount >= MAX_PHOTOS ? "text-common-subtext cursor-not-allowed opacity-50" : "text-common-blue700 cursor-pointer"
              }`}
            >
              <input {...getInputProps()} />
              {t("insurance.addPhotos")}
            </div>

            <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
              {keptImages.map((image) => (
                <PhotoTile
                  key={image.object_name ?? image.link}
                  src={image.link}
                  onRemove={() => setKeptImages((current) => current.filter((item) => item !== image))}
                />
              ))}
              {newFiles.map((file, index) => (
                <NewPhotoTile
                  key={`${file.name}-${index}`}
                  file={file}
                  onRemove={() => setNewFiles((current) => current.filter((_, idx) => idx !== index))}
                />
              ))}
            </div>
          </div>
        </div>
      }
    />
  );
}

function PhotoTile({ src, onRemove }: { src?: string; onRemove: () => void }) {
  return (
    <div className="relative">
      <img className="w-full aspect-square rounded-lg object-cover" src={src} alt="" />
      <button
        type="button"
        onClick={onRemove}
        className="absolute top-1 right-1 w-6 h-6 rounded-full bg-common-white text-common-red500 text-sm leading-none shadow"
        aria-label="remove"
      >
        ×
      </button>
    </div>
  );
}

function NewPhotoTile({ file, onRemove }: { file: File; onRemove: () => void }) {
  const [url, setUrl] = useState<string>();

  useEffect(() => {
    const objectUrl = URL.createObjectURL(file);
    setUrl(objectUrl);

    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);

  return <PhotoTile src={url} onRemove={onRemove} />;
}
